import React, { useState, useEffect } from "react";
import { Icon } from "../../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider, ScaleBadge,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

import { PageHead } from "./dashboard.jsx";
import { KPIMini } from "./orgchart.jsx";
import { Agrupaciones } from "./agrupaciones.jsx";

/* ============================================================
   Talention — Escalas (rangos de puntuación)
   ============================================================ */

function ScalesTalention() {
  const { state, dispatch } = useStore();
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const sorted = [...state.scales].sort((a, b) => b.min - a.min);
  const maxRange = Math.max(...state.scales.map(s => s.max));

  // Los puestos se agrupan por su escala OFICIAL: la fijada a mano manda sobre
  // la calculada, así que una excepción cuenta en su escala y no en la del rango.
  const positionsByScale = {};
  const counts = {};
  const manualCounts = {};
  let manualTotal = 0;
  let unclassified = 0;
  state.positions.forEach(p => {
    const { scale, manual, reason } = TALENTION_HELPERS.resolveScaleForPosition(p, state.scales);
    if (!scale) {
      // Solo chirría que un puesto ya finalizado se quede sin banda.
      if (p.status === "FINALIZADA") unclassified++;
      return;
    }
    counts[scale.id] = (counts[scale.id] || 0) + 1;
    if (manual) {
      manualCounts[scale.id] = (manualCounts[scale.id] || 0) + 1;
      manualTotal++;
    }
    if (!positionsByScale[scale.id]) positionsByScale[scale.id] = [];
    positionsByScale[scale.id].push({ position: p, manual, reason });
  });
  const classifiedTotal = Object.values(counts).reduce((s, n) => s + n, 0);

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Escalas Salariales"
        sub="Cada rango de puntuación se localiza en una escala. Las escalas determinan la banda salarial del puesto."
        actions={<Button variant="primary" icon="plus" onClick={() => setEditing("new")}>Nueva escala</Button>}
      />

      <div className="grid grid-4">
        <KPIMini label="Escalas definidas"    value={sorted.length}     icon="scale"     color="#7c3aed" />
        <KPIMini label="Puestos clasificados" value={classifiedTotal}   icon="briefcase" color="#0891b2" />
        <KPIMini label="Escala fijada a mano" value={manualTotal}       icon="pin"       color="#dc2626" />
        <KPIMini label="Sin clasificar"       value={unclassified}      icon="alert"     color="#d97706" />
      </div>

      {manualTotal > 0 && (
        <div className="banner warn">
          <Icon name="alert" size={16} className="banner-icon" />
          <div className="tiny" style={{ flex: 1 }}>
            <b>{manualTotal} puesto{manualTotal !== 1 ? "s" : ""} con la escala fijada a mano.</b> Son excepciones
            documentadas al cálculo por puntuación: no se recalculan aunque cambien los puntos o los rangos.
            Revísalas de vez en cuando para que no se acumulen.
          </div>
        </div>
      )}

      {/* Visual ladder */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{
          padding: "13px 20px", background: "var(--bg-soft)", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--ink-900)", color: "white", display: "grid", placeItems: "center" }}>
              <Icon name="scale" size={14} />
            </div>
            <span style={{ fontWeight: 650, fontSize: 13.5, letterSpacing: "-0.015em" }}>Escalera de escalas</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 11.5, color: "var(--ink-400)" }}>rango 0 – {maxRange}</span>
            <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 999, background: "var(--ink-100)", color: "var(--ink-600)", fontWeight: 600 }}>{sorted.length} escalas</span>
          </div>
        </div>
        <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
          {sorted.map((s, i) => {
            const segWidth = ((s.max - s.min + 1) / maxRange) * 100;
            const leftPct = (s.min / maxRange) * 100;
            const count = counts[s.id] || 0;
            const manualCount = manualCounts[s.id] || 0;
            return (
              <div key={s.id} style={{ display: "flex", gap: 14, padding: "8px 4px", alignItems: "center" }}>
                <div className="row" style={{ gap: 10, minWidth: 170, flexShrink: 0 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: s.color + "18", color: s.color,
                    display: "grid", placeItems: "center",
                    fontWeight: 700, fontSize: 11.5,
                    flexShrink: 0,
                    border: `1px solid ${s.color}25`,
                  }}>{s.id < 10 ? "0" + s.id : s.id}</div>
                  <div>
                    <div className="semi" style={{ fontSize: 13.5 }}>{s.name}</div>
                    <div className="tiny muted">{s.label}</div>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ position: "relative", height: 26 }}>
                    <div style={{ position: "absolute", inset: 0, background: "var(--ink-100)", borderRadius: 6 }} />
                    <div style={{
                      position: "absolute", left: leftPct + "%", width: segWidth + "%",
                      top: 0, bottom: 0,
                      background: `linear-gradient(90deg, ${s.color}cc, ${s.color})`,
                      borderRadius: 6,
                      boxShadow: "0 1px 0 rgba(0,0,0,0.05) inset",
                    }} />
                  </div>
                  <div style={{ position: "relative", height: 16, marginTop: 2 }}>
                    <span style={{
                      position: "absolute", left: leftPct + "%",
                      fontSize: 10.5, fontFamily: "var(--font-mono)",
                      color: "var(--ink-400)", whiteSpace: "nowrap",
                    }}>{s.min}</span>
                    <span style={{
                      position: "absolute", left: Math.min(leftPct + segWidth, 98) + "%",
                      transform: "translateX(-100%)",
                      fontSize: 10.5, fontFamily: "var(--font-mono)",
                      color: "var(--ink-400)", whiteSpace: "nowrap",
                    }}>{s.max}</span>
                  </div>
                </div>
                <div className="row" style={{ gap: 8, minWidth: 170, justifyContent: "flex-end", flexShrink: 0 }}>
                  {manualCount > 0 && (
                    <Badge tone="warn" style={{ borderStyle: "dashed", borderColor: "currentColor" }}>
                      <Icon name="pin" size={11} /> {manualCount} a mano
                    </Badge>
                  )}
                  <Badge tone={count > 0 ? "good" : ""}>{count} puesto{count !== 1 ? "s" : ""}</Badge>
                  <button className="icon-btn" onClick={() => setEditing(s)}><Icon name="edit" size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Table view */}
      <Card className="flush">
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
          <div className="card-title">Detalle de escalas</div>
        </div>
        <table className="t-table">
          <thead><tr>
            <th style={{ width: 60 }}>ID</th>
            <th>Denominación</th>
            <th>Banda</th>
            <th>Rango Inicial</th>
            <th>Rango Final</th>
            <th>Color</th>
            <th>Puestos en escala</th>
            <th style={{ width: 100 }}>Acciones</th>
          </tr></thead>
          <tbody>
            {sorted.map(s => (
              <tr key={s.id}>
                <td><span className="row-id">#{s.id}</span></td>
                <td><b>{s.name}</b></td>
                <td className="muted">{s.label}</td>
                <td className="mono semi">{s.min}</td>
                <td className="mono semi">{s.max}</td>
                <td>
                  <div className="scale-chip">
                    <span className="swatch" style={{ background: s.color }} />
                    <span className="mono tiny">{s.color}</span>
                  </div>
                </td>
                <td>
                  <div className="row" style={{ gap: 6 }}>
                    <Badge tone={counts[s.id] ? "good" : ""}>{counts[s.id] || 0}</Badge>
                    {manualCounts[s.id] > 0 && (
                      <Badge tone="warn" style={{ borderStyle: "dashed", borderColor: "currentColor" }}>
                        <Icon name="pin" size={11} /> {manualCounts[s.id]} a mano
                      </Badge>
                    )}
                  </div>
                </td>
                <td>
                  <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    {counts[s.id] > 0 && <button className="icon-btn" title="Ver puestos" onClick={() => setViewing(s)}><Icon name="eye" size={14} /></button>}
                    <button className="icon-btn" onClick={() => setEditing(s)}><Icon name="edit" size={14} /></button>
                    <button className="icon-btn" onClick={() => {
                      // Borrar la escala devuelve al cálculo automático a los puestos
                      // fijados a ella, y con ellos se pierde el motivo escrito a mano:
                      // hay que decirlo antes, no después.
                      const fijados = state.positions.filter(p => p.scaleOverrideId === s.id).length;
                      const aviso = fijados > 0
                        ? `¿Eliminar la escala "${s.name}"?\n\n${fijados} puesto${fijados !== 1 ? "s" : ""} tiene${fijados !== 1 ? "n" : ""} esta escala fijada a mano: volverá${fijados !== 1 ? "n" : ""} al cálculo automático y se perderá el motivo de la excepción.`
                        : `¿Eliminar la escala "${s.name}"?`;
                      if (confirm(aviso)) dispatch({ type: "DELETE_SCALE", id: s.id });
                    }}><Icon name="trash" size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <ScaleModal
        open={editing !== null}
        scale={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          if (editing === "new") dispatch({ type: "ADD_SCALE", scale: data });
          else dispatch({ type: "UPDATE_SCALE", id: editing.id, patch: data });
          setEditing(null);
        }}
      />

      <ScalePositionsModal
        open={viewing !== null}
        scale={viewing}
        entries={viewing ? (positionsByScale[viewing.id] || []) : []}
        state={state}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}

function ScaleModal({ open, scale, onClose, onSave }) {
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(100);
  const [color, setColor] = useState("#0a84ff");
  useEffect(() => {
    setName(scale?.name || "");
    setLabel(scale?.label || "");
    setMin(scale?.min ?? 0);
    setMax(scale?.max ?? 100);
    setColor(scale?.color || "#0a84ff");
  }, [open, scale]);

  const presets = ["#16a34a", "#eab308", "#7c3aed", "#dc2626", "#0ea5e9", "#ec4899", "#f97316", "#1e3a8a", "#06b6d4", "#059669", "#a16207", "#6b7280"];

  // Un rango vacío o del revés rompe la clasificación por puntos y la escalera
  // visual (anchos NaN), así que se valida antes de dejar guardar.
  const parsedMin = parseInt(min, 10);
  const parsedMax = parseInt(max, 10);
  const blockedReason =
    !name.trim() ? "Indica una denominación para la escala."
    : !Number.isFinite(parsedMin) || !Number.isFinite(parsedMax) ? "El rango inicial y el rango final deben ser números."
    : parsedMin > parsedMax ? "El rango inicial no puede ser mayor que el rango final."
    : "";
  const canSave = blockedReason === "";

  return (
    <Modal open={open} onClose={onClose} title={scale ? "Editar escala" : "Nueva escala"}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!canSave} title={blockedReason}
            onClick={() => canSave && onSave({ name, label, min: parsedMin, max: parsedMax, color })}>
            {scale ? "Guardar" : "Crear escala"}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="grid grid-2">
          <Field label="Denominación"><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Escala 01" autoFocus /></Field>
          <Field label="Etiqueta de banda"><input className="input" value={label} onChange={e => setLabel(e.target.value)} placeholder="Ej. Directivo" /></Field>
        </div>
        <div className="grid grid-2">
          <Field label="Rango inicial"><input type="number" className="input" value={min} onChange={e => setMin(e.target.value)} /></Field>
          <Field label="Rango final"><input type="number" className="input" value={max} onChange={e => setMax(e.target.value)} /></Field>
        </div>
        <Field label="Color">
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {presets.map(c => (
              <button key={c} onClick={() => setColor(c)}
                style={{
                  width: 32, height: 32, borderRadius: 8, background: c,
                  border: color === c ? "3px solid var(--brand-500)" : "1px solid var(--border)",
                  cursor: "pointer", padding: 0,
                }} />
            ))}
            <input type="color" value={color} onChange={e => setColor(e.target.value)}
              style={{ width: 32, height: 32, border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", padding: 0 }} />
          </div>
        </Field>
        {canSave ? (
          <div className="banner info" style={{ marginTop: 6 }}>
            <Icon name="info" size={16} className="banner-icon" />
            <div className="tiny" style={{ flex: 1 }}>
              Cualquier puesto cuya puntuación SVPT total esté entre <b>{parsedMin}</b> y <b>{parsedMax}</b> se asignará a esta escala automáticamente.
              Los puestos con la escala <b>fijada a mano</b> son una excepción documentada y no se recalculan aunque cambies el rango:
              hay que revisarlos uno a uno.
            </div>
          </div>
        ) : (
          <div className="banner warn" style={{ marginTop: 6 }}>
            <Icon name="alert" size={16} className="banner-icon" />
            <div className="tiny" style={{ flex: 1 }}>
              <b>No se puede guardar.</b> {blockedReason}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* `entries` son { position, manual, reason }: la asignación importa tanto como el puesto. */
function ScalePositionsModal({ open, scale, entries, state, onClose }) {
  if (!scale) return null;
  const manualCount = entries.filter(e => e.manual).length;

  return (
    <Modal open={open} onClose={onClose} title={`Puestos en ${scale.name}`} width={620}
      footer={<Button variant="ghost" onClick={onClose}>Cerrar</Button>}
    >
      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 10, padding: "10px 14px", borderRadius: 12, background: scale.color + "12", border: `1px solid ${scale.color}25` }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: scale.color + "25", color: scale.color, display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12 }}>
            {entries.length}
          </div>
          <div style={{ flex: 1 }}>
            <div className="semi" style={{ fontSize: 14, color: scale.color }}>{scale.name} · {scale.label}</div>
            <div className="tiny muted">Rango {scale.min} – {scale.max} pts</div>
          </div>
          {manualCount > 0 && (
            <Badge tone="warn" style={{ borderStyle: "dashed", borderColor: "currentColor" }}>
              <Icon name="pin" size={11} /> {manualCount} a mano
            </Badge>
          )}
        </div>

        {entries.length === 0 ? (
          <div className="empty">Sin puestos clasificados en esta escala</div>
        ) : (
          <table className="t-table">
            <thead><tr>
              <th style={{ width: 80 }}>Código</th>
              <th>Puesto</th>
              <th>Departamento</th>
              <th style={{ width: 80 }}>Pts SVPT</th>
              <th style={{ width: 190 }}>Asignación</th>
            </tr></thead>
            <tbody>
              {entries.map(({ position: p, manual, reason }) => {
                const dept = state.departments.find(d => d.id === p.departmentId);
                return (
                  <tr key={p.id}>
                    <td><span className="row-id mono">{p.code}</span></td>
                    <td><b>{p.name}</b></td>
                    <td className="muted">{dept?.name || "—"}</td>
                    <td><span className="mono semi" style={{ color: scale.color }}>{p.points}</span></td>
                    <td>
                      {manual ? (
                        <div className="stack" style={{ gap: 3, alignItems: "flex-start" }}>
                          <ScaleBadge scale={scale} manual reason={reason} />
                          <span className="tiny muted" style={{ textWrap: "pretty" }}>
                            Manual · {reason || "sin motivo registrado"}
                          </span>
                        </div>
                      ) : (
                        <span className="tiny muted">Automática por puntuación</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

/* Con el método Ministerio no hay escalas propias: la pantalla muestra las
   agrupaciones oficiales. */
function Scales() {
  const { state } = useStore();
  return state.metodo === "ministerio" ? <Agrupaciones /> : <ScalesTalention />;
}

export { Scales };